import {
  BadRequestException,
  Body,
  Controller,
  Get,
  Injectable,
  Module,
  Patch,
} from '@nestjs/common';
import { ApiBearerAuth, ApiTags } from '@nestjs/swagger';
import { Role } from '@sakura/prisma/enums';
import { IsOptional, IsString, MaxLength } from 'class-validator';
import {
  CurrentUser,
  CurrentUserData,
  Public,
  Roles,
} from '../common/security';
import { PrismaService } from '../prisma/prisma.module';

const SETTINGS_ID = 'main';

class UpdateAppSettingsDto {
  @IsOptional()
  @IsString()
  @MaxLength(160)
  projectName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(80)
  shortName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(240)
  tagline?: string;

  @IsOptional()
  @IsString()
  @MaxLength(2_000_000)
  logoDataUrl?: string;

  @IsOptional()
  @IsString()
  address?: string;

  @IsOptional()
  @IsString()
  @MaxLength(40)
  phone?: string;

  @IsOptional()
  @IsString()
  @MaxLength(160)
  email?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  website?: string;

  @IsOptional()
  @IsString()
  @MaxLength(240)
  footerText?: string;
}

@Injectable()
class AppSettingsService {
  constructor(private readonly prisma: PrismaService) {}

  get() {
    return this.prisma.appSetting.upsert({
      where: { id: SETTINGS_ID },
      update: {},
      create: {
        id: SETTINGS_ID,
        projectName: 'Sakura Pharma',
        shortName: 'Sakura',
        tagline: 'Pharmacy Management & MIS',
        footerText: '© 2026 Sakura Pharma',
      },
    });
  }

  update(dto: UpdateAppSettingsDto, userId: string) {
    const projectName = dto.projectName?.trim();
    const shortName = dto.shortName?.trim();
    if (dto.projectName !== undefined && !projectName) {
      throw new BadRequestException('Project name is required');
    }
    if (dto.shortName !== undefined && !shortName) {
      throw new BadRequestException('Short name is required');
    }
    if (dto.logoDataUrl?.trim() && !dto.logoDataUrl.startsWith('data:image/')) {
      throw new BadRequestException('Logo must be a valid image');
    }

    const nullable = (value: string | undefined) =>
      value === undefined ? undefined : value.trim() || null;
    return this.prisma.appSetting.upsert({
      where: { id: SETTINGS_ID },
      update: {
        projectName,
        shortName,
        tagline: nullable(dto.tagline),
        logoDataUrl: nullable(dto.logoDataUrl),
        address: nullable(dto.address),
        phone: nullable(dto.phone),
        email: nullable(dto.email),
        website: nullable(dto.website),
        footerText: nullable(dto.footerText),
        updatedById: userId,
      },
      create: {
        id: SETTINGS_ID,
        projectName: projectName ?? 'Sakura Pharma',
        shortName: shortName ?? 'Sakura',
        tagline: nullable(dto.tagline),
        logoDataUrl: nullable(dto.logoDataUrl),
        address: nullable(dto.address),
        phone: nullable(dto.phone),
        email: nullable(dto.email),
        website: nullable(dto.website),
        footerText: nullable(dto.footerText),
        updatedById: userId,
      },
    });
  }
}

@ApiTags('app-settings')
@Controller('app-settings')
class AppSettingsController {
  constructor(private readonly service: AppSettingsService) {}

  @Public()
  @Get()
  get() {
    return this.service.get();
  }

  @ApiBearerAuth()
  @Roles(Role.SUPER_ADMIN, Role.ADMIN)
  @Patch()
  update(
    @Body() dto: UpdateAppSettingsDto,
    @CurrentUser() user: CurrentUserData,
  ) {
    return this.service.update(dto, user.id);
  }
}

@Module({
  controllers: [AppSettingsController],
  providers: [AppSettingsService],
})
export class SettingsModule {}
